import type { ActionSheetButton } from "@capacitor/action-sheet";
import { ActionSheet, ActionSheetButtonStyle } from "@capacitor/action-sheet";
import { Filesystem } from "@capacitor/filesystem";
import type { PickFilesResult } from "@capawesome/capacitor-file-picker";
import { FilePicker } from "@capawesome/capacitor-file-picker";
import type { PickedFile } from "@capawesome/capacitor-file-picker/dist/esm/definitions";
import { useMemo } from "react";
import type FileType from "src/constants/fileType";
import { addSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import humanizeFileSize from "src/lib/humanizeFileSize";
import { isHybridApp } from "src/lib/platform";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import { fileState } from "src/state/state";
import BlockingLoadingOverlayController from "src/ui/components/BlockingLoadingOverlay/BlockingLoadingOverlayController";

interface ActionSheetButtonWithHandler extends ActionSheetButton {
  handler?: () => void;
}

export interface FilesPickerParams {
  fileTypes?: string[];
  setFileDetails?: (
    fileDetails: {
      name: string;
      type: "img" | "pdf";
      size: string;
    } | null
  ) => void;
  onSelect?: (file: File) => void;
  setLoading?: (loading: boolean) => void;
  onChange?: (fileId: string) => void;
  setPreviewImageSrc?: (src: string | null) => void;
  namePrefix?: string;
  metaFileType?: FileType;
  tags?: string[];
  maxSize?: number;
  imageRatio?: number;
  camera?: {
    title?: string;
    description?: string;
  };
  /**
   * If true, files will be stored locally and not uploaded immediately.
   * Use uploadDeferredFiles() to upload them later.
   */
  deferUpload?: boolean;
}

export class FilesPicker {
  params: FilesPickerParams;
  loader?: ReturnType<typeof BlockingLoadingOverlayController.startLoading>;
  static readonly defaultMaxSize = 20;
  static readonly defaultImageRatio = 1.414;
  static readonly defaultFileTypes = ["*/*"];
  private deferredFiles: Map<string, File> = new Map();
  private uploadStatus: Map<string, "pending" | "success" | "error"> =
    new Map();

  constructor(params: FilesPickerParams) {
    this.params = params;
  }

  get maxSize() {
    return this.params.maxSize ?? FilesPicker.defaultMaxSize;
  }

  get imageRatio() {
    return this.params.imageRatio ?? FilesPicker.defaultImageRatio;
  }

  get fileTypes() {
    return this.params.fileTypes ?? FilesPicker.defaultFileTypes;
  }

  deviceHasCamera = async (): Promise<boolean> => {
    const videoInputDevices = await navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        return devices.filter((device) => device.kind === "videoinput");
      });

    return videoInputDevices.length > 0;
  };

  sanitizeFileName = (fileName: string): string => {
    return fileName
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Remove diacritical marks
      .replace(/[^a-zA-Z0-9.-]/g, "_") // Replace any other special characters with underscore
      .replace(/_+/g, "_") // Replace multiple consecutive underscores with a single one
      .replace(/^_+|_+$/g, ""); // Remove leading and trailing underscores
  };

  uploadFile = async (
    file: File,
    options: {
      handlePreviewUrl?: (url: string | null) => void;
      fileKey?: string;
    }
  ) => {
    const fileName = this.sanitizeFileName(file.name);

    // If deferred upload is enabled, store the file and show preview
    if (this.params.deferUpload) {
      const fileKey = options.fileKey ?? fileName;
      this.deferredFiles.set(fileKey, file);
      this.uploadStatus.set(fileKey, "pending");

      this.params.setFileDetails?.({
        name: fileName,
        type: file.type.includes("image") ? "img" : "pdf",
        size: humanizeFileSize(file.size)
      });

      this.params.onSelect?.(file);

      const blobUrl = URL.createObjectURL(file);
      options.handlePreviewUrl?.(blobUrl);

      // Return a temporary ID to track the file
      this.params.onChange?.(`deferred-${fileKey}`);
      return;
    }

    // Original immediate upload logic
    BlockingLoadingOverlayController.startLoading({ bg: "transparent" });
    try {
      this.params.setFileDetails?.({
        name: fileName,
        type: file.type.includes("image") ? "img" : "pdf",
        size: humanizeFileSize(file.size)
      });

      this.params.onSelect?.(file);

      // handle upload
      this.params.setLoading?.(true);

      const blobUrl = URL.createObjectURL(file);
      options.handlePreviewUrl?.(blobUrl);

      const nameWithPrefix = this.params.namePrefix
        ? `${this.params.namePrefix} - ${fileName}`
        : fileName;

      const result = await fileState.uploadFile({
        file,
        fileAttributes: {
          "file.type": this.params.metaFileType,
          "file.name": nameWithPrefix,
          "file.tags": this.params.tags
        }
      });

      if (result) {
        this.params.onChange?.(result.id);
        BlockingLoadingOverlayController.loadingSuccess();
      } else {
        options.handlePreviewUrl?.(null);
        this.params.setFileDetails?.(null);
        BlockingLoadingOverlayController.loadingError({
          title: translate("error_unknown_error")
        });
        reportErrorSentry(new Error("Failed to upload file"), {
          fileTypeId: this.params.metaFileType,
          name: nameWithPrefix,
          tags: this.params.tags
        });
      }
    } catch (e) {
      reportErrorSentry(e);
      this.params.setFileDetails?.(null);
      BlockingLoadingOverlayController.loadingError({
        title: translate("error_unknown_error")
      });
    }

    this.params.setLoading?.(false);
  };

  parsePickedFilesResult = async (
    result?: PickFilesResult
  ): Promise<File | undefined> => {
    const selected = result?.files[0];

    if (selected?.blob) {
      return new File([selected.blob], selected.name, {
        type: selected.mimeType
      });
    } else if (selected?.data) {
      const blob = FilesPicker.base64toBlob(selected.data, selected.mimeType);
      return new File([blob], selected.name, {
        type: selected.mimeType
      });
    } else if (selected?.path) {
      const readResult = await Filesystem.readFile({
        path: selected.path
      });

      const { data } = readResult;
      let blob: Blob | null = null;
      if (typeof data === "string") {
        blob = FilesPicker.base64toBlob(data, selected.mimeType);
      } else if (typeof data === "object") {
        blob = data;
      }

      if (blob) {
        return new File([blob], selected.name, {
          type: selected.mimeType
        });
      }
    }
  };

  validatePickedFile = (result: PickFilesResult): boolean => {
    const file = result.files[0] as PickedFile | undefined;

    if (!file) {
      return false;
    }

    // check file size
    if (file.size > this.maxSize * 1024 * 1024) {
      return false;
    }

    return true;
  };

  handleImagePicked = (result: PickFilesResult, fileKey?: string) => {
    const valid = this.validatePickedFile(result);
    if (!valid) {
      return;
    }

    void this.parsePickedFilesResult(result).then((file) => {
      if (file) {
        void this.uploadFile(file, {
          handlePreviewUrl: this.params.setPreviewImageSrc,
          fileKey
        });
      }
    });
  };

  handleFilePicked = (result: PickFilesResult, fileKey?: string) => {
    const valid = this.validatePickedFile(result);
    if (!valid) {
      return;
    }

    const [selected] = result.files;

    //     check if its an image file
    if (selected.mimeType.includes("image")) {
      this.handleImagePicked(result, fileKey);
      return;
    }

    void this.parsePickedFilesResult(result).then((file) => {
      if (file) {
        this.params.setPreviewImageSrc?.(null);
        void this.uploadFile(file, { fileKey });
      }
    });
  };

  pickImageFromGallery = () => {
    void FilePicker.pickImages({
      limit: 1
    })
      .then(this.handleImagePicked)
      .catch((e: unknown) =>
        addSentryBreadcrumb("error", "Error picking image", "error", { e })
      );
  };

  pickFile = () => {
    void FilePicker.pickFiles({
      limit: 1,
      types: this.fileTypes
    })
      .then(this.handleFilePicked)
      .catch((e: unknown) =>
        addSentryBreadcrumb("error", "Error picking file", "error", { e })
      );
  };

  createGalleryOption =
    async (): Promise<ActionSheetButtonWithHandler | null> => {
      const allowedTypes = this.fileTypes.filter(
        (type) => type.includes("image") || type.includes("*/*")
      );

      if (!isHybridApp() || allowedTypes.length === 0) {
        return null;
      }

      return {
        title: translate("chooseFromGallery"),
        handler: this.pickImageFromGallery
      };
    };

  createUploadFileOption =
    async (): Promise<ActionSheetButtonWithHandler | null> => {
      return {
        title: translate("uploadFile"),
        handler: this.pickFile
      };
    };

  startPicker = async (options: { title?: string; message?: string } = {}) => {
    const buttonPromises = Promise.all([
      this.createGalleryOption(),
      this.createUploadFileOption()
    ]);

    const buttons = (await buttonPromises).filter(Boolean);

    if (buttons.length === 1) {
      buttons[0]?.handler?.();
      return;
    }

    const result = await ActionSheet.showActions({
      title: options.title,
      message: options.message,
      options: [
        ...buttons.map((button) => ({
          title: button?.title ?? "",
          style: ActionSheetButtonStyle.Default
        })),
        {
          title: translate("cancel"),
          style: ActionSheetButtonStyle.Cancel
        }
      ]
    });

    const selectedButton = buttons[result.index] as
      | ActionSheetButtonWithHandler
      | undefined;

    if (selectedButton?.handler) {
      selectedButton.handler();
    } else {
      BlockingLoadingOverlayController.endLoading();
    }
  };

  /**
   * Upload all deferred files that were stored locally in parallel.
   * Returns a map of fileKey to uploaded fileId.
   * @param showLoadingOverlay - If true, shows loading overlay (default: true)
   */
  uploadDeferredFiles = async (
    showLoadingOverlay = true
  ): Promise<Map<string, string>> => {
    const uploadedFiles = new Map<string, string>();

    if (this.deferredFiles.size === 0) {
      return uploadedFiles;
    }

    if (showLoadingOverlay) {
      BlockingLoadingOverlayController.startLoading({ bg: "transparent" });
    }

    // Create an array of upload promises that handle individual failures
    const uploadPromises = Array.from(this.deferredFiles.entries()).map(
      async ([fileKey, file]) => {
        try {
          const fileName = this.sanitizeFileName(file.name);
          const nameWithPrefix = this.params.namePrefix
            ? `${this.params.namePrefix} - ${fileName}`
            : fileName;

          const result = await fileState.uploadFile({
            file,
            fileAttributes: {
              "file.type": this.params.metaFileType,
              "file.name": nameWithPrefix,
              "file.tags": this.params.tags
            }
          });

          if (result) {
            this.uploadStatus.set(fileKey, "success");
            return { fileKey, fileId: result.id, success: true };
          } else {
            this.uploadStatus.set(fileKey, "error");
            reportErrorSentry(new Error(`Failed to upload file: ${fileName}`));
            return { fileKey, fileId: "", success: false };
          }
        } catch (e) {
          this.uploadStatus.set(fileKey, "error");
          reportErrorSentry(e);
          return { fileKey, fileId: "", success: false };
        }
      }
    );

    // Wait for all uploads to complete (including failures)
    const results = await Promise.allSettled(uploadPromises);

    // Build the result map and track failures
    const failures: string[] = [];
    results.forEach((result) => {
      if (result.status === "fulfilled" && result.value.success) {
        uploadedFiles.set(result.value.fileKey, result.value.fileId);
      } else if (result.status === "fulfilled" && !result.value.success) {
        failures.push(result.value.fileKey);
      }
    });

    // Remove successfully uploaded files from deferred list
    uploadedFiles.forEach((_, fileKey) => {
      this.deferredFiles.delete(fileKey);
    });

    if (showLoadingOverlay) {
      if (failures.length > 0) {
        // Some files failed - show error
        BlockingLoadingOverlayController.loadingError({
          title: translate("error_unknown_error")
        });
      } else {
        // All files succeeded
        BlockingLoadingOverlayController.loadingSuccess();
        this.uploadStatus.clear();
      }
    }

    return uploadedFiles;
  };

  /**
   * Get the number of deferred files waiting to be uploaded
   */
  getDeferredFilesCount = (): number => {
    return this.deferredFiles.size;
  };

  /**
   * Clear all deferred files without uploading
   */
  clearDeferredFiles = (): void => {
    this.deferredFiles.clear();
    this.uploadStatus.clear();
  };

  /**
   * Get the upload status of a specific file
   */
  getUploadStatus = (
    fileKey: string
  ): "pending" | "success" | "error" | undefined => {
    return this.uploadStatus.get(fileKey);
  };

  /**
   * Retry uploading a specific failed file
   */
  retryUpload = async (fileKey: string): Promise<string | null> => {
    const file = this.deferredFiles.get(fileKey);
    if (!file) {
      return null;
    }

    try {
      this.uploadStatus.set(fileKey, "pending");
      this.params.setLoading?.(true);

      const fileName = this.sanitizeFileName(file.name);
      const nameWithPrefix = this.params.namePrefix
        ? `${this.params.namePrefix} - ${fileName}`
        : fileName;

      const result = await fileState.uploadFile({
        file,
        fileAttributes: {
          "file.type": this.params.metaFileType,
          "file.name": nameWithPrefix,
          "file.tags": this.params.tags
        }
      });

      if (result) {
        this.uploadStatus.set(fileKey, "success");
        this.deferredFiles.delete(fileKey);
        this.params.onChange?.(result.id);
        this.params.setLoading?.(false);
        return result.id;
      } else {
        this.uploadStatus.set(fileKey, "error");
        this.params.setLoading?.(false);
        return null;
      }
    } catch (e) {
      reportErrorSentry(e);
      this.uploadStatus.set(fileKey, "error");
      this.params.setLoading?.(false);
      return null;
    }
  };

  static base64toBlob(base64Data: string, contentType: string): Blob {
    contentType = contentType || "";
    const sliceSize = 1024;
    const byteCharacters = atob(base64Data);
    const bytesLength = byteCharacters.length;
    const slicesCount = Math.ceil(bytesLength / sliceSize);
    const byteArrays = new Array(slicesCount);

    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
      const begin = sliceIndex * sliceSize;
      const end = Math.min(begin + sliceSize, bytesLength);

      const bytes = new Array(end - begin);
      for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
        bytes[i] = byteCharacters[offset].charCodeAt(0);
      }
      byteArrays[sliceIndex] = new Uint8Array(bytes);
    }
    return new Blob(byteArrays, { type: contentType });
  }
}

export function useFilesPicker(params: FilesPickerParams) {
  // Destructure params to avoid recreating picker on every render
  const {
    fileTypes,
    setFileDetails,
    onSelect,
    setLoading,
    onChange,
    setPreviewImageSrc,
    namePrefix,
    metaFileType,
    tags,
    maxSize,
    imageRatio,
    camera,
    deferUpload
  } = params;

  const picker = useMemo(
    () =>
      new FilesPicker({
        fileTypes,
        setFileDetails,
        onSelect,
        setLoading,
        onChange,
        setPreviewImageSrc,
        namePrefix,
        metaFileType,
        tags,
        maxSize,
        imageRatio,
        camera,
        deferUpload
      }),
    // Only recreate picker if deferUpload or other critical params change
    [deferUpload, namePrefix, metaFileType]
  );

  return {
    pickFile: picker.pickImageFromGallery,
    deviceHasCamera: picker.deviceHasCamera,
    startPicker: picker.startPicker,
    uploadDeferredFiles: picker.uploadDeferredFiles,
    getDeferredFilesCount: picker.getDeferredFilesCount,
    clearDeferredFiles: picker.clearDeferredFiles,
    getUploadStatus: picker.getUploadStatus,
    retryUpload: picker.retryUpload,
    picker
  };
}
